import type { GridPoint } from "../types/grid";
import type { LevelData } from "../types/level";
import type { LevelState } from "../model/LevelState";
import {
  getEntityDrawInstructions,
  type EntityVisibilityOverride,
} from "./draw-model";

const ENTITY_DEPTH = 1;
const FOREGROUND_DEPTH = 3;

export class EntityRenderer {
  private readonly entityGraphics: Phaser.GameObjects.Graphics;
  private readonly playerGraphics: Phaser.GameObjects.Graphics;
  private readonly exitGraphics: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene) {
    this.entityGraphics = scene.add.graphics();
    this.playerGraphics = scene.add.graphics();
    this.exitGraphics = scene.add.graphics();
  }

  public render(
    state: LevelState,
    data: LevelData,
    playerOverride?: GridPoint,
    flashPlayer?: boolean,
    hideNonPlayerEntities?: boolean,
    visibilityOverride?: EntityVisibilityOverride,
  ): void {
    this.entityGraphics.clear();
    this.playerGraphics.clear();
    this.exitGraphics.clear();

    const instructions = getEntityDrawInstructions(
      data,
      state,
      playerOverride,
      flashPlayer,
      hideNonPlayerEntities,
      visibilityOverride,
    );
    for (const inst of instructions) {
      if (inst.kind === "breadcrumb") {
        const cx = inst.x + inst.width / 2;
        const cy = inst.y + inst.height / 2;
        const half = inst.width / 2;
        this.entityGraphics.fillStyle(inst.color);
        this.entityGraphics.fillPoints(
          [
            { x: cx, y: cy - half },
            { x: cx + half, y: cy },
            { x: cx, y: cy + half },
            { x: cx - half, y: cy },
          ],
          true,
          true,
        );
      } else {
        const target = inst.zIndex >= FOREGROUND_DEPTH
          ? (inst.kind === "player" ? this.playerGraphics : this.exitGraphics)
          : this.entityGraphics;
        target.fillStyle(inst.color);
        target.fillRect(inst.x, inst.y, inst.width, inst.height);
      }
    }

    this.entityGraphics.setDepth(ENTITY_DEPTH);
    this.playerGraphics.setDepth(FOREGROUND_DEPTH);
    this.exitGraphics.setDepth(FOREGROUND_DEPTH);

    // Set alpha for player/exit based on fog state
    // Find the player and exit instructions to get their fogAlpha
    for (const inst of instructions) {
      if (inst.kind === "player") {
        this.playerGraphics.setAlpha(inst.fogAlpha > 0 ? 0.5 : 1);
      }
      if (inst.kind === "exit") {
        this.exitGraphics.setAlpha(inst.fogAlpha > 0 ? 0.5 : 1);
      }
    }
  }

  /** Apply a mask to the entity layer (collectibles/portals only) */
  public setMask(mask: Phaser.Display.Masks.BitmapMask): void {
    this.entityGraphics.setMask(mask);
  }

  public destroy(): void {
    this.entityGraphics.destroy();
    this.playerGraphics.destroy();
    this.exitGraphics.destroy();
  }
}
